import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { checkPermission, checkPermissionsBatch } from '../lib/opa-client';

/**
 * Hook for checking a single permission
 */
export function usePermission(page: string, action: string): {
  isAllowed: boolean;
  isLoading: boolean;
} {
  const { user } = useAuth();
  const [isAllowed, setIsAllowed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    if (!user) {
      setIsAllowed(false);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    checkPermission(user, page, action)
      .then(setIsAllowed)
      .finally(() => setIsLoading(false));
  }, [user, page, action]);
  
  return { isAllowed, isLoading };
}

/**
 * Hook for checking multiple permissions at once
 */
export function usePermissions(checks: Array<{ page: string; action: string }>): {
  permissions: Record<string, boolean>;
  isLoading: boolean;
} {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    if (!user) {
      setPermissions({});
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    checkPermissionsBatch(user, checks)
      .then(setPermissions)
      .finally(() => setIsLoading(false));
  }, [user, JSON.stringify(checks)]);
  
  return { permissions, isLoading };
}

/**
 * Hook for checking page-level access (read permission)
 */
export function usePageAccess(page: string): {
  hasAccess: boolean;
  isLoading: boolean;
} {
  const result = usePermission(page, 'read');
  return {
    hasAccess: result.isAllowed,
    isLoading: result.isLoading,
  };
}

